import { ArrowRight, Key as KeyIcon, Plus, RefreshCw, Search } from "lucide-react";
import * as React from "react";
import { Link, useNavigate } from "react-router-dom";

import { useKeysQuery } from "@/entities/keys/queries";
import { useProjectsListQuery } from "@/entities/projects/queries";
import { useAuthStore } from "@/shared/lib/auth-store";
import { Button } from "@/shared/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/select";
import { PageHeader } from "@/widgets/PageHeader";
import { ProjectsList } from "@/widgets/ProjectsList";
import { WordstatExpenseWidget } from "@/widgets/WordstatExpenseWidget";

type Filter = "active" | "archived" | "all";

const FILTER_TO_ARCHIVED: Record<Filter, boolean | null> = {
  active: false,
  archived: true,
  all: null,
};

export function DashboardPage(): JSX.Element {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const keysQ = useKeysQuery();
  // На дашборде «можно создавать проект» = есть Yandex. Без OpenAI
  // создание тоже доступно (с типом «запрос»), форма проекта сама
  // решит блокировать запуск или нет в зависимости от input_type.
  const yandexReady = !!keysQ.data?.yandex.valid;
  const openaiReady = !!keysQ.data?.openai.valid;
  const canCreateProject = yandexReady;

  const [filter, setFilter] = React.useState<Filter>("active");
  const [rawQuery, setRawQuery] = React.useState("");
  const [search, setSearch] = React.useState<string | null>(null);

  // Дебаунс поиска 300мс.
  React.useEffect(() => {
    const t = setTimeout(() => {
      setSearch(rawQuery.trim() || null);
    }, 300);
    return () => clearTimeout(t);
  }, [rawQuery]);

  // Для кнопки «Обновить» — дергаем оба источника.
  const listQ = useProjectsListQuery({
    archived: FILTER_TO_ARCHIVED[filter],
    q: search,
    limit: 50,
    offset: 0,
  });

  return (
    <>
      <PageHeader
        title={<>Мои <span className="text-gradient">проекты</span></>}
        subtitle="Соберите семантику один раз — потом делайте из неё любые срезы"
        actions={
          <>
            <Button
              type="button"
              variant="outline"
              onClick={() => listQ.refetch()}
              disabled={listQ.isFetching}
              className="border-white/15 bg-white/[0.04] text-white hover:bg-white/[0.08]"
            >
              <RefreshCw className="h-4 w-4" /> Обновить
            </Button>
            <Button
              type="button"
              onClick={() => navigate("/projects/new")}
              className="bg-gradient-primary text-white shadow-glow-purple hover:shadow-glow-purple-lg"
              disabled={!canCreateProject}
              title={
                !canCreateProject
                  ? "Сначала настройте Yandex API-ключ"
                  : "Создать новый проект"
              }
            >
              <Plus className="h-4 w-4" /> Новый проект
            </Button>
          </>
        }
      />

      {user && !user.is_approved && (
        <div className="mb-6 rounded-xl border border-amber/30 bg-amber/[0.07] p-4 text-sm">
          Аккаунт ещё не одобрен — некоторые операции могут быть недоступны.
        </div>
      )}

      {keysQ.data && !yandexReady && (
        <div className="mb-6 flex items-center gap-4 rounded-2xl border border-amber/30 bg-gradient-to-r from-amber/10 to-pink/[0.05] p-4 sm:p-5">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-amber to-amber-dark text-white shadow-glow-amber">
            <KeyIcon className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-white">
              Подключите Yandex API-ключ, чтобы создавать проекты
            </div>
            <div className="mt-0.5 text-xs text-muted-foreground">
              Yandex Cloud (Wordstat) обязателен для сбора. OpenAI
              опционален — нужен только для типов «ниша»/«сайт» и для
              генерации идей в анализах.
            </div>
          </div>
          <Link
            to="/settings/keys"
            className="inline-flex items-center gap-1 rounded-full border border-white/15 bg-white/[0.04] px-4 py-2 text-sm font-semibold text-white transition hover:bg-white/[0.08]"
          >
            Настроить <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      )}

      {keysQ.data && yandexReady && !openaiReady && (
        <div className="mb-6 flex items-center gap-4 rounded-2xl border border-white/15 bg-white/[0.04] p-3 sm:p-4">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/[0.06] text-muted-foreground">
            <KeyIcon className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1 text-xs text-muted-foreground">
            OpenAI-ключ не настроен — это нормально. Сбор по типу «запрос»
            работает без него. Добавьте, если хотите «нишу»/«сайт» или
            генерацию идей в анализах.
          </div>
          <Link
            to="/settings/keys"
            className="hidden shrink-0 items-center gap-1 text-xs text-cyan hover:underline sm:inline-flex"
          >
            Настроить <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
      )}

      <div className="mb-6">
        <WordstatExpenseWidget />
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="flex flex-1 items-center gap-2 rounded-lg border border-white/10 bg-white/[0.04] px-3">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input
            value={rawQuery}
            onChange={(e) => setRawQuery(e.target.value)}
            placeholder="Поиск по проектам…"
            className="h-9 min-w-0 flex-1 bg-transparent text-sm text-white outline-none placeholder:text-muted-foreground"
          />
        </div>
        <Select value={filter} onValueChange={(v) => setFilter(v as Filter)}>
          <SelectTrigger className="w-[160px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Активные</SelectItem>
            <SelectItem value="archived">В архиве</SelectItem>
            <SelectItem value="all">Все</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <ProjectsList
        archived={FILTER_TO_ARCHIVED[filter]}
        q={search}
      />
    </>
  );
}
